<script setup>
import {
  reactive,
  computed,
  ref,
  watch,
  nextTick,
  onMounted,
  onUnmounted,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, requiredIf } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import CampaignPreview from 'dashboard/components-next/Marketing/CampaignPreview.vue';
import EmojiPickerButton from 'dashboard/components-next/Marketing/EmojiPickerButton.vue';
import WhatsappTemplateDialog from 'dashboard/components-next/Marketing/WhatsappTemplateDialog.vue';
import {
  MEDIA_KINDS,
  CAROUSEL_MAX_ITEMS,
  inferMediaKind,
  isVideoFile,
} from '../../../ochat/marketing/previewFrame';

const emit = defineEmits(['created']);

const { t } = useI18n();
const store = useStore();

// Lo que acepta el selector. Ya no depende del destino: el servidor convierte a
// JPEG lo que Instagram necesite (SocialPosts::MetaImageService).
const ACCEPTED_MEDIA =
  'image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp,video/mp4,video/quicktime,.mp4,.mov,.m4v';
// The WhatsApp number of this installation is bridged through an API inbox.
const WHATSAPP_CHANNEL_TYPES = [INBOX_TYPES.WHATSAPP, INBOX_TYPES.API];

const uiFlags = useMapGetter('marketing/getUIFlags');
const labels = useMapGetter('labels/getLabels');
const allInboxes = useMapGetter('inboxes/getInboxes');
const templateEntry = useMapGetter('marketing/getTemplateEntryForInbox');
const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const initialState = {
  title: '',
  caption: '',
  link: '',
  scheduledAt: null,
  whatsappInboxId: null,
  templateKey: null,
};

const state = reactive({ ...initialState });
const mediaFiles = ref([]);
const mediaPreviewUrls = ref([]);
// Ancho y alto de cada imagen elegida, en el mismo orden. La vista previa los
// necesita para dibujar la forma real; null mientras se leen o si es un video.
const mediaSizes = ref([]);
const imageFit = ref('original');
// El encabezado de la plantilla de WhatsApp y la vista previa siguen razonando
// sobre un solo archivo: de un carrusel es el primero.
const imageFile = computed(() => mediaFiles.value[0] ?? null);
const imagePreviewUrl = computed(() => mediaPreviewUrls.value[0] ?? '');
const imageError = ref('');
// Aviso de calidad: no bloquea el envio, solo le dice al operador que Instagram
// va a tocar la pieza.
const imageWarning = ref('');
const selectedPostTargets = ref([]);
const selectedLabels = ref([]);
const bodyParams = reactive({});
const sendOverWhatsApp = ref(false);
const fileInputRef = ref(null);
const captionRef = ref(null);
const coverFile = ref(null);
const coverInputRef = ref(null);

// El texto y la imagen son del posteo, no de WhatsApp: solo se exigen cuando hay
// un destino de red social elegido.
const rules = {
  caption: { required: requiredIf(() => selectedPostTargets.value.length > 0) },
  scheduledAt: { required },
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => uiFlags.value.isCreating);
const isSyncing = computed(() => uiFlags.value.isSyncingTemplates);
const isCreatingTemplate = computed(() => uiFlags.value.isCreatingTemplate);
const templateDialogRef = ref(null);

const canPublishPosts = computed(() =>
  isFeatureEnabledonAccount.value(accountId.value, FEATURE_FLAGS.SOCIAL_POSTS)
);

const currentDateTime = computed(() => {
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

// One option per publishable Meta surface. A Facebook page with a linked
// Instagram professional account yields two.
const postTargetOptions = computed(() => {
  if (!canPublishPosts.value) return [];
  const options = [];

  (allInboxes.value ?? []).forEach(inbox => {
    if (inbox.channel_type === INBOX_TYPES.FB) {
      options.push({
        key: `${inbox.id}-facebook`,
        inboxId: inbox.id,
        platform: 'facebook',
        label: `${inbox.name} · Facebook`,
      });

      if (inbox.instagram_id) {
        options.push({
          key: `${inbox.id}-instagram`,
          inboxId: inbox.id,
          platform: 'instagram',
          label: `${inbox.name} · Instagram`,
        });
      }
    }

    if (inbox.channel_type === INBOX_TYPES.INSTAGRAM) {
      options.push({
        key: `${inbox.id}-instagram`,
        inboxId: inbox.id,
        platform: 'instagram',
        label: `${inbox.name} · Instagram`,
      });
    }
  });

  return options;
});

const whatsappInboxOptions = computed(() =>
  (allInboxes.value ?? [])
    .filter(inbox => WHATSAPP_CHANNEL_TYPES.includes(inbox.channel_type))
    .map(inbox => ({ value: inbox.id, label: inbox.name }))
);

const labelOptions = computed(
  () =>
    labels.value?.map(label => ({ value: label.id, label: label.title })) ?? []
);

const currentEntry = computed(() =>
  state.whatsappInboxId ? templateEntry.value(state.whatsappInboxId) : null
);

const availableTemplates = computed(() => currentEntry.value?.templates ?? []);

// Only a Cloud API number owns a WABA to create templates on; the YCloud-bridged
// ones are managed on YCloud's side, so the button stays hidden for those.
const canCreateTemplate = computed(
  () => currentEntry.value?.supports_template_creation === true
);

// More than one WABA means the same name can exist twice, so the WABA is shown
// to avoid picking a template that does not belong to that number.
const showsWaba = computed(
  () => new Set(availableTemplates.value.map(tpl => tpl.waba_id)).size > 1
);

const templateOptions = computed(() =>
  availableTemplates.value.map(template => ({
    value: `${template.name}|${template.language}`,
    label: showsWaba.value
      ? `${template.name} (${template.language}) · ${template.waba_id}`
      : `${template.name} (${template.language})`,
  }))
);

// What to tell the operator when the picker is empty: the real reason, not a
// generic "nothing synced".
const templatesProblem = computed(() => {
  const entry = currentEntry.value;
  if (!entry || availableTemplates.value.length > 0) return '';

  if (entry.last_error) {
    return t('MARKETING.CAMPAIGN.FORM.WHATSAPP.SYNC_ERROR', {
      error: entry.last_error,
    });
  }

  const unusable = Object.entries(entry.unusable_by_status ?? {});
  if (unusable.length) {
    return t('MARKETING.CAMPAIGN.FORM.WHATSAPP.UNUSABLE_TEMPLATES', {
      detail: unusable
        .map(([status, count]) => `${count} ${status}`)
        .join(', '),
    });
  }

  return t('MARKETING.CAMPAIGN.FORM.WHATSAPP.NO_TEMPLATES');
});

const selectedTemplate = computed(() => {
  if (!state.templateKey) return null;
  const [name, language] = state.templateKey.split('|');
  return availableTemplates.value.find(
    template => template.name === name && template.language === language
  );
});

const componentOfType = type =>
  selectedTemplate.value?.components?.find(
    component => component.type?.toUpperCase() === type
  );

const templateBodyText = computed(() => componentOfType('BODY')?.text ?? '');

// The header image is a per-message parameter, so a template with an image
// header reuses the campaign image and never asks for another one.
const templateUsesImageHeader = computed(() => {
  const header = componentOfType('HEADER');
  return header?.format?.toUpperCase() === 'IMAGE';
});

// Variables are read off the body text: {{1}} positional or {{name}} named.
const templateVariables = computed(() => {
  const matches = templateBodyText.value.match(/{{\s*([\w\d_]+)\s*}}/g) ?? [];
  return [...new Set(matches.map(match => match.replace(/[{}\s]/g, '')))];
});

const isWhatsAppSelected = computed(
  () => sendOverWhatsApp.value && Boolean(state.whatsappInboxId)
);

const renderedTemplatePreview = computed(() =>
  templateVariables.value.reduce(
    (text, variable) =>
      text.replace(
        new RegExp(`{{\\s*${variable}\\s*}}`, 'g'),
        bodyParams[variable] || `{{${variable}}}`
      ),
    templateBodyText.value
  )
);

// El tipo de pieza sale de lo que se eligió, en vez de pedirlo antes: elegir
// "Carrusel" y subir una sola foto, o "Imagen" y subir un video, era un error
// que el operador no tenía por qué poder cometer.
const mediaInference = computed(() => inferMediaKind(mediaFiles.value));
const mediaKind = computed(() => mediaInference.value.kind);
const isReel = computed(() => mediaKind.value === MEDIA_KINDS.REEL);
const hasImages = computed(
  () =>
    mediaKind.value === MEDIA_KINDS.IMAGE ||
    mediaKind.value === MEDIA_KINDS.CAROUSEL
);

const isInstagramSelected = computed(() =>
  postTargetOptions.value.some(
    option =>
      option.platform === 'instagram' &&
      selectedPostTargets.value.includes(option.key)
  )
);

const mediaHint = computed(() =>
  isReel.value
    ? t('MARKETING.CAMPAIGN.FORM.IMAGE.HINT_REEL')
    : t('MARKETING.CAMPAIGN.FORM.IMAGE.HINT')
);

const selectedPostTargetLabels = computed(() =>
  postTargetOptions.value
    .filter(option => selectedPostTargets.value.includes(option.key))
    .map(option => option.label)
);

// Las redes elegidas, sin repetir: la vista previa arma una pestaña por red.
const selectedPlatforms = computed(() => [
  ...new Set(
    postTargetOptions.value
      .filter(option => selectedPostTargets.value.includes(option.key))
      .map(option => option.platform)
  ),
]);

// Claves escritas enteras y no armadas con el código de error: el lint de i18n
// solo puede verificar que existan si están escritas.
const mediaErrorMessage = error =>
  ({
    MIXED: t('MARKETING.CAMPAIGN.FORM.IMAGE.ERROR_MIXED'),
    MULTIPLE_VIDEOS: t('MARKETING.CAMPAIGN.FORM.IMAGE.ERROR_MULTIPLE_VIDEOS'),
    UNSUPPORTED: t('MARKETING.CAMPAIGN.FORM.IMAGE.ERROR_UNSUPPORTED'),
    TOO_MANY: t('MARKETING.CAMPAIGN.FORM.IMAGE.ERROR_CAROUSEL_TOO_MANY', {
      max: CAROUSEL_MAX_ITEMS,
    }),
  })[error];

const validateImage = () => {
  imageError.value = '';

  // Instagram y Facebook no publican sin imagen. WhatsApp solo la necesita si la
  // plantilla lleva encabezado de imagen.
  // Not `required`: that name is the vuelidate validator imported at the top of
  // the file, and shadowing it fails the no-shadow rule in the pre-commit hook.
  const imageIsRequired =
    selectedPostTargets.value.length > 0 || templateUsesImageHeader.value;

  if (!mediaFiles.value.length) {
    if (!imageIsRequired) return true;
    imageError.value = t('MARKETING.CAMPAIGN.FORM.IMAGE.ERROR_REQUIRED');
    return false;
  }

  const { error } = mediaInference.value;
  if (error) {
    imageError.value = mediaErrorMessage(error);
    return false;
  }

  return true;
};

// Por debajo de 320 px de ancho no hay nada que recuperar: ampliar solo agrega
// borrosidad. Es el único aviso que queda; la proporción la resuelve el servidor
// y la muestra la vista previa.
const MIN_INSTAGRAM_WIDTH = 320;

const readImageSize = file =>
  new Promise(resolve => {
    const objectUrl = URL.createObjectURL(file);
    const probe = new Image();
    probe.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({ width: probe.naturalWidth, height: probe.naturalHeight });
    };
    probe.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(null);
    };
    probe.src = objectUrl;
  });

const checkImageQuality = () => {
  imageWarning.value = '';
  if (!hasImages.value || !isInstagramSelected.value) return;

  const narrowest = mediaSizes.value
    .filter(Boolean)
    .reduce((min, size) => Math.min(min, size.width), Infinity);
  if (narrowest >= MIN_INSTAGRAM_WIDTH) return;

  imageWarning.value = t('MARKETING.CAMPAIGN.FORM.IMAGE.WARNING_TOO_SMALL', {
    width: narrowest,
    min: MIN_INSTAGRAM_WIDTH,
  });
};

const revokePreview = () => {
  mediaPreviewUrls.value.forEach(url => URL.revokeObjectURL(url));
  mediaPreviewUrls.value = [];
};

const coverPreviewUrl = ref('');

const handleCoverChange = event => {
  if (coverPreviewUrl.value) URL.revokeObjectURL(coverPreviewUrl.value);
  [coverFile.value] = Array.from(event.target.files ?? []);
  coverPreviewUrl.value = coverFile.value
    ? URL.createObjectURL(coverFile.value)
    : '';
};

const clearCover = () => {
  if (coverPreviewUrl.value) URL.revokeObjectURL(coverPreviewUrl.value);
  coverPreviewUrl.value = '';
  coverFile.value = null;
  if (coverInputRef.value) coverInputRef.value.value = '';
};

// Después de agregar o quitar archivos. Sin archivos no se muestra el error de
// "subí una imagen": quien acaba de sacar la última foto no se equivocó.
const refreshMediaState = () => {
  // La portada solo existe para un reel: si ya no hay video, se va.
  if (!isReel.value) clearCover();
  imageWarning.value = '';
  if (!mediaFiles.value.length) {
    imageError.value = '';
    return;
  }
  if (validateImage()) checkImageQuality();
};

// Elegir archivos agrega a lo que ya hay en vez de reemplazarlo, para poder
// sumar una foto que faltaba sin volver a elegir todas. El orden es el de
// llegada, que es el que Meta respeta en un carrusel.
const handleFileChange = async event => {
  const added = Array.from(event.target.files ?? []);
  // Vaciar el input deja volver a elegir el mismo archivo después de quitarlo.
  if (fileInputRef.value) fileInputRef.value.value = '';
  if (!added.length) return;

  mediaFiles.value = [...mediaFiles.value, ...added];
  mediaPreviewUrls.value = [
    ...mediaPreviewUrls.value,
    ...added.map(file => URL.createObjectURL(file)),
  ];
  const addedSizes = await Promise.all(
    added.map(file => (isVideoFile(file) ? null : readImageSize(file)))
  );
  mediaSizes.value = [...mediaSizes.value, ...addedSizes];
  refreshMediaState();
};

// Para la foto que se eligió por error o que en la vista previa queda mal.
const removeMedia = index => {
  URL.revokeObjectURL(mediaPreviewUrls.value[index]);
  mediaFiles.value = mediaFiles.value.filter((_, i) => i !== index);
  mediaPreviewUrls.value = mediaPreviewUrls.value.filter((_, i) => i !== index);
  mediaSizes.value = mediaSizes.value.filter((_, i) => i !== index);
  refreshMediaState();
};

const openFilePicker = () => fileInputRef.value?.click();

const isPostTargetSelected = key => selectedPostTargets.value.includes(key);

const togglePostTarget = key => {
  selectedPostTargets.value = isPostTargetSelected(key)
    ? selectedPostTargets.value.filter(item => item !== key)
    : [...selectedPostTargets.value, key];
};

const hasDestination = computed(
  () => selectedPostTargets.value.length > 0 || isWhatsAppSelected.value
);

// Ticking WhatsApp without finishing its fields blocks the submit rather than
// silently dropping the channel.
const isWhatsAppIncomplete = computed(
  () =>
    sendOverWhatsApp.value &&
    (!state.whatsappInboxId ||
      !selectedTemplate.value ||
      selectedLabels.value.length === 0 ||
      templateVariables.value.some(variable => !bodyParams[variable]))
);

const isPostIncomplete = computed(
  () =>
    selectedPostTargets.value.length > 0 && (!state.caption || !imageFile.value)
);

// Un reel no sirve de encabezado de plantilla: ahi va una imagen o no va nada.
const needsImageForTemplate = computed(
  () =>
    isWhatsAppSelected.value &&
    templateUsesImageHeader.value &&
    (isReel.value || !imageFile.value)
);

const isSubmitDisabled = computed(
  () =>
    v$.value.$invalid ||
    !hasDestination.value ||
    !!imageError.value ||
    isPostIncomplete.value ||
    needsImageForTemplate.value ||
    isWhatsAppIncomplete.value
);

// El TextArea de upstream no expone el elemento, pero si el nodo raiz, asi que
// se baja hasta el textarea para insertar donde esta el cursor en vez de pegar
// el emoji al final de todo el texto.
const insertEmojiInCaption = emoji => {
  const textarea = captionRef.value?.$el?.querySelector('textarea');
  if (!textarea) {
    state.caption += emoji;
    return;
  }

  const { selectionStart, selectionEnd } = textarea;
  state.caption =
    state.caption.slice(0, selectionStart) +
    emoji +
    state.caption.slice(selectionEnd);

  // Dejar el cursor despues del emoji, para poder seguir escribiendo.
  nextTick(() => {
    const cursor = selectionStart + emoji.length;
    textarea.focus();
    textarea.setSelectionRange(cursor, cursor);
  });
};

// Las variables de plantilla son valores cortos --un nombre, una fecha-- asi que
// el emoji va al final y no hace falta seguir el cursor.
const appendEmojiToVariable = (variable, emoji) => {
  bodyParams[variable] = `${bodyParams[variable] ?? ''}${emoji}`;
};

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

// Matches the nested shape Whatsapp::TemplateProcessorService consumes. The
// header media_url is filled in by the backend at delivery time, because signed
// storage URLs expire.
const buildTemplateParams = () => ({
  name: selectedTemplate.value.name,
  language: selectedTemplate.value.language,
  category: selectedTemplate.value.category || 'MARKETING',
  namespace: selectedTemplate.value.namespace || '',
  processed_params: {
    body: templateVariables.value.reduce(
      (params, variable) => ({ ...params, [variable]: bodyParams[variable] }),
      {}
    ),
  },
});

const buildFormData = () => {
  const formData = new FormData();
  formData.append('campaign[title]', state.title);
  formData.append('campaign[caption]', state.caption);
  formData.append('campaign[link]', state.link);
  formData.append(
    'campaign[scheduled_at]',
    formatToUTCString(state.scheduledAt)
  );
  mediaFiles.value.forEach(file => formData.append('campaign[media][]', file));
  if (coverFile.value) formData.append('campaign[cover]', coverFile.value);
  formData.append('campaign[image_fit]', imageFit.value);

  postTargetOptions.value
    .filter(option => isPostTargetSelected(option.key))
    .forEach((option, index) => {
      formData.append(`campaign[targets][${index}][inbox_id]`, option.inboxId);
      formData.append(`campaign[targets][${index}][platform]`, option.platform);
    });

  if (isWhatsAppSelected.value) {
    formData.append('campaign[whatsapp_inbox_ids][]', state.whatsappInboxId);
    // El cuerpo de la plantilla con las variables ya reemplazadas: es lo que el
    // agente tiene que leer en la conversación.
    formData.append('campaign[whatsapp_body]', renderedTemplatePreview.value);
    selectedLabels.value.forEach(id =>
      formData.append('campaign[label_ids][]', id)
    );

    const templateParams = buildTemplateParams();
    ['name', 'language', 'category', 'namespace'].forEach(key => {
      formData.append(`campaign[template_params][${key}]`, templateParams[key]);
    });
    Object.entries(templateParams.processed_params.body).forEach(
      ([key, value]) => {
        formData.append(
          `campaign[template_params][processed_params][body][${key}]`,
          value
        );
      }
    );
  }

  return formData;
};

const resetState = () => {
  Object.assign(state, initialState);
  revokePreview();
  mediaFiles.value = [];
  mediaSizes.value = [];
  imageFit.value = 'original';
  clearCover();
  imageError.value = '';
  imageWarning.value = '';
  selectedPostTargets.value = [];
  selectedLabels.value = [];
  sendOverWhatsApp.value = false;
  Object.keys(bodyParams).forEach(key => delete bodyParams[key]);
  if (fileInputRef.value) fileInputRef.value.value = '';
  v$.value.$reset();
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid || !validateImage()) return;

  try {
    await store.dispatch('marketing/createCampaign', buildFormData());
    useAlert(t('MARKETING.CAMPAIGN.API.CREATE_SUCCESS'));
    resetState();
    emit('created');
  } catch (error) {
    // Los dos formatos que devuelve el servidor: `error` lo arma el controlador
    // a mano y `message` sale de render_record_invalid, que es el que trae el
    // motivo de una validación del modelo. Leer solo `error` dejaba al operador
    // con un "no se pudo crear" sin decirle nunca qué estaba mal.
    const response = error?.response?.data;
    useAlert(
      response?.error ||
        response?.message ||
        t('MARKETING.CAMPAIGN.API.CREATE_ERROR')
    );
  }
};

const openTemplateDialog = () => {
  templateDialogRef.value.reset();
  templateDialogRef.value.dialogRef.open();
};

const handleCreateTemplate = async template => {
  try {
    const created = await store.dispatch('marketing/createWhatsAppTemplate', {
      inboxId: state.whatsappInboxId,
      template,
    });
    templateDialogRef.value.dialogRef.close();
    // Not preselected: Meta returns it PENDING and the picker only offers
    // approved templates.
    useAlert(
      t('MARKETING.CAMPAIGN.API.TEMPLATE_CREATED', { name: created.name })
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('MARKETING.CAMPAIGN.API.TEMPLATE_CREATE_ERROR')
    );
  }
};

const handleSyncTemplates = async () => {
  try {
    await store.dispatch('marketing/syncWhatsAppTemplates');
    useAlert(t('MARKETING.CAMPAIGN.API.SYNC_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('MARKETING.CAMPAIGN.API.SYNC_ERROR')
    );
  }
};

// Picking another inbox invalidates the template choice: templates belong to a
// WhatsApp Business account.
watch(
  () => state.whatsappInboxId,
  () => {
    state.templateKey = null;
  }
);

// With a single WhatsApp number there is nothing to choose, so it is preselected.
watch(sendOverWhatsApp, enabled => {
  if (!enabled) {
    state.whatsappInboxId = null;
    state.templateKey = null;
    selectedLabels.value = [];
  } else if (whatsappInboxOptions.value.length === 1) {
    state.whatsappInboxId = whatsappInboxOptions.value[0].value;
  }
});

// El aviso de ancho mínimo es de Instagram: aparece o se va al tildar esa red.
watch(isInstagramSelected, checkImageQuality);

watch(templateVariables, variables => {
  Object.keys(bodyParams).forEach(key => {
    if (!variables.includes(key)) delete bodyParams[key];
  });
  variables.forEach(variable => {
    if (bodyParams[variable] === undefined) bodyParams[variable] = '';
  });
});

onMounted(() => store.dispatch('marketing/fetchWhatsAppTemplates'));
onUnmounted(revokePreview);
</script>

<template>
  <div class="grid gap-6 lg:gap-8 lg:grid-cols-[minmax(0,1fr)_22rem]">
    <form class="flex flex-col gap-6 min-w-0" @submit.prevent="handleSubmit">
      <!-- Datos de la campaña: sólo lo que sirve para las dos vías -->
      <section class="flex flex-col gap-4">
        <div class="flex flex-col gap-0.5">
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t('MARKETING.CAMPAIGN.FORM.SECTIONS.CONTENT') }}
          </h3>
          <p class="text-xs text-n-slate-11">
            {{ t('MARKETING.CAMPAIGN.FORM.SECTIONS.CONTENT_HINT') }}
          </p>
        </div>

        <Input
          v-model="state.title"
          :label="t('MARKETING.CAMPAIGN.FORM.TITLE.LABEL')"
          :placeholder="t('MARKETING.CAMPAIGN.FORM.TITLE.PLACEHOLDER')"
          :message="t('MARKETING.CAMPAIGN.FORM.TITLE.HINT')"
        />

        <div class="flex flex-col gap-1">
          <label for="image" class="mb-0.5 text-sm font-medium text-n-slate-12">
            {{ t('MARKETING.CAMPAIGN.FORM.IMAGE.LABEL') }}
          </label>
          <!-- El input nativo queda oculto: su "3 archivos" dejaba de ser cierto
               en cuanto se quitaba uno, y no agregaba nada a las miniaturas. -->
          <input
            id="image"
            ref="fileInputRef"
            type="file"
            :accept="ACCEPTED_MEDIA"
            multiple
            class="hidden"
            @change="handleFileChange"
          />
          <div class="flex flex-wrap items-center gap-2">
            <div
              v-for="(url, index) in mediaPreviewUrls"
              :key="url"
              class="relative w-16 h-16 overflow-hidden rounded-lg border border-n-weak bg-n-alpha-black2"
            >
              <video
                v-if="isVideoFile(mediaFiles[index])"
                :src="url"
                class="object-cover w-full h-full"
                muted
                preload="metadata"
              />
              <img
                v-else
                :src="url"
                :alt="`${index + 1}`"
                class="object-cover w-full h-full"
              />
              <button
                type="button"
                class="absolute flex items-center justify-center rounded-full top-0.5 ltr:right-0.5 rtl:left-0.5 size-5 bg-n-alpha-black1 text-n-slate-12 hover:bg-n-ruby-9"
                :title="t('MARKETING.CAMPAIGN.FORM.IMAGE.REMOVE')"
                @click="removeMedia(index)"
              >
                <span class="i-lucide-x size-3" />
              </button>
            </div>
            <Button
              type="button"
              variant="faded"
              color="slate"
              size="sm"
              icon="i-lucide-plus"
              :label="
                mediaFiles.length
                  ? t('MARKETING.CAMPAIGN.FORM.IMAGE.ADD_MORE')
                  : t('MARKETING.CAMPAIGN.FORM.IMAGE.ADD')
              "
              @click="openFilePicker"
            />
          </div>
          <p v-if="imageError" class="text-xs text-n-ruby-9">
            {{ imageError }}
          </p>
          <p v-else-if="imageWarning" class="text-xs text-n-amber-11">
            {{ imageWarning }}
          </p>
          <p
            v-if="!imageError && !imageWarning"
            class="text-xs text-n-slate-11"
          >
            {{ mediaHint }}
          </p>

          <!-- Despues del texto de ayuda del video y no antes: puesto arriba,
               ese texto parecia hablar de la portada. -->
          <div v-if="isReel" class="flex flex-col gap-1 mt-2">
            <label for="cover" class="text-sm font-medium text-n-slate-12">
              {{ t('MARKETING.CAMPAIGN.FORM.COVER.LABEL') }}
            </label>
            <input
              id="cover"
              ref="coverInputRef"
              type="file"
              accept="image/jpeg,.jpg,.jpeg"
              class="text-sm text-n-slate-11"
              @change="handleCoverChange"
            />
            <p class="text-xs text-n-slate-11">
              {{ t('MARKETING.CAMPAIGN.FORM.COVER.HINT') }}
            </p>
          </div>

          <label
            v-if="hasImages && selectedPostTargets.length > 0"
            class="flex items-start gap-2 mt-2 cursor-pointer"
          >
            <Checkbox
              :model-value="imageFit === 'fill'"
              @update:model-value="imageFit = $event ? 'fill' : 'original'"
            />
            <span class="flex flex-col gap-0.5">
              <span class="text-sm text-n-slate-12">
                {{ t('MARKETING.CAMPAIGN.FORM.IMAGE.FIT_LABEL') }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{ t('MARKETING.CAMPAIGN.FORM.IMAGE.FIT_HINT') }}
              </span>
            </span>
          </label>
        </div>
      </section>

      <!-- Posteo en redes: el texto y el link son de acá, no de WhatsApp -->
      <section
        v-if="canPublishPosts"
        class="flex flex-col gap-4 pt-4 border-t border-n-weak"
      >
        <div class="flex flex-col gap-0.5">
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t('MARKETING.CAMPAIGN.FORM.SECTIONS.POST') }}
          </h3>
          <p class="text-xs text-n-slate-11">
            {{ t('MARKETING.CAMPAIGN.FORM.SECTIONS.POST_HINT') }}
          </p>
        </div>

        <p
          v-if="postTargetOptions.length === 0"
          class="text-xs text-n-amber-11"
        >
          {{ t('MARKETING.CAMPAIGN.FORM.POST_TARGETS.EMPTY') }}
        </p>
        <label
          v-for="option in postTargetOptions"
          :key="option.key"
          class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer"
        >
          <Checkbox
            :model-value="isPostTargetSelected(option.key)"
            @update:model-value="togglePostTarget(option.key)"
          />
          {{ option.label }}
        </label>

        <template v-if="selectedPostTargets.length > 0">
          <TextArea
            ref="captionRef"
            v-model="state.caption"
            :label="t('MARKETING.CAMPAIGN.FORM.CAPTION.LABEL')"
            :placeholder="t('MARKETING.CAMPAIGN.FORM.CAPTION.PLACEHOLDER')"
            :message="
              v$.caption.$error
                ? t('MARKETING.CAMPAIGN.FORM.CAPTION.ERROR')
                : ''
            "
            :message-type="v$.caption.$error ? 'error' : 'info'"
            :max-length="2200"
            show-character-count
            auto-height
          >
            <EmojiPickerButton
              class="absolute z-10 ltr:right-2 rtl:left-2 top-2"
              @select="insertEmojiInCaption"
            />
          </TextArea>

          <Input
            v-model="state.link"
            :label="t('MARKETING.CAMPAIGN.FORM.LINK.LABEL')"
            :placeholder="t('MARKETING.CAMPAIGN.FORM.LINK.PLACEHOLDER')"
            :message="t('MARKETING.CAMPAIGN.FORM.LINK.HINT')"
          />
        </template>
      </section>

      <!-- WhatsApp -->
      <section class="flex flex-col gap-3 pt-4 border-t border-n-weak">
        <label
          class="flex items-center gap-2 text-sm font-medium text-n-slate-12 cursor-pointer"
        >
          <Checkbox v-model="sendOverWhatsApp" />
          {{ t('MARKETING.CAMPAIGN.FORM.WHATSAPP.LABEL') }}
        </label>
        <p v-if="sendOverWhatsApp" class="text-xs text-n-slate-11">
          {{ t('MARKETING.CAMPAIGN.FORM.WHATSAPP.CONTENT_HINT') }}
        </p>

        <template v-if="sendOverWhatsApp">
          <ComboBox
            v-model="state.whatsappInboxId"
            :options="whatsappInboxOptions"
            :placeholder="
              t('MARKETING.CAMPAIGN.FORM.WHATSAPP.INBOX_PLACEHOLDER')
            "
            class="[&>div>button]:bg-n-alpha-black2"
          />
        </template>

        <template v-if="isWhatsAppSelected">
          <ComboBox
            v-if="templateOptions.length"
            v-model="state.templateKey"
            :options="templateOptions"
            :placeholder="
              t('MARKETING.CAMPAIGN.FORM.WHATSAPP.TEMPLATE_PLACEHOLDER')
            "
            class="[&>div>button]:bg-n-alpha-black2"
          />

          <div v-if="templatesProblem" class="flex flex-col gap-2">
            <p class="text-xs text-n-amber-11">{{ templatesProblem }}</p>
          </div>

          <div class="flex flex-wrap gap-2 self-start">
            <Button
              variant="faded"
              color="slate"
              size="sm"
              type="button"
              icon="i-lucide-refresh-cw"
              :label="t('MARKETING.CAMPAIGN.FORM.WHATSAPP.SYNC')"
              :is-loading="isSyncing"
              :disabled="isSyncing"
              @click="handleSyncTemplates"
            />

            <Button
              v-if="canCreateTemplate"
              variant="faded"
              color="slate"
              size="sm"
              type="button"
              icon="i-lucide-plus"
              :label="t('MARKETING.CAMPAIGN.FORM.WHATSAPP.NEW_TEMPLATE.OPEN')"
              @click="openTemplateDialog"
            />
          </div>

          <template v-if="selectedTemplate">
            <p v-if="templateUsesImageHeader" class="text-xs text-n-teal-11">
              {{ t('MARKETING.CAMPAIGN.FORM.WHATSAPP.IMAGE_HEADER_NOTICE') }}
            </p>

            <div
              v-for="variable in templateVariables"
              :key="variable"
              class="flex items-end gap-1"
            >
              <Input
                v-model="bodyParams[variable]"
                class="flex-1"
                :label="`{{${variable}}}`"
                :placeholder="
                  t('MARKETING.CAMPAIGN.FORM.WHATSAPP.VARIABLE_PLACEHOLDER')
                "
              />
              <EmojiPickerButton
                position="top"
                class="mb-1"
                @select="appendEmojiToVariable(variable, $event)"
              />
            </div>
          </template>

          <TagMultiSelectComboBox
            v-model="selectedLabels"
            :options="labelOptions"
            :label="t('MARKETING.CAMPAIGN.FORM.WHATSAPP.LABELS')"
            :placeholder="
              t('MARKETING.CAMPAIGN.FORM.WHATSAPP.LABELS_PLACEHOLDER')
            "
            class="[&>div>button]:bg-n-alpha-black2"
          />
        </template>
      </section>

      <!-- Programación -->
      <section class="flex flex-col gap-3 pt-4 border-t border-n-weak">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('MARKETING.CAMPAIGN.FORM.SECTIONS.SCHEDULE') }}
        </h3>
        <Input
          v-model="state.scheduledAt"
          type="datetime-local"
          :min="currentDateTime"
          :message="
            v$.scheduledAt.$error
              ? t('MARKETING.CAMPAIGN.FORM.SCHEDULED_AT.ERROR')
              : ''
          "
          :message-type="v$.scheduledAt.$error ? 'error' : 'info'"
        />
      </section>

      <div class="flex flex-col sm:flex-row gap-3 sm:items-center">
        <Button
          class="w-full sm:w-auto"
          :label="t('MARKETING.CAMPAIGN.FORM.BUTTONS.CREATE')"
          type="submit"
          :is-loading="isCreating"
          :disabled="isCreating || isSubmitDisabled"
        />
        <Button
          class="w-full sm:w-auto"
          variant="faded"
          color="slate"
          type="button"
          :label="t('MARKETING.CAMPAIGN.FORM.BUTTONS.CLEAR')"
          @click="resetState"
        />
      </div>
    </form>

    <CampaignPreview
      class="lg:sticky lg:top-4 lg:self-start"
      :media-urls="mediaPreviewUrls"
      :media-sizes="mediaSizes"
      :media-kind="mediaKind"
      :cover-url="coverPreviewUrl"
      :fill="imageFit === 'fill'"
      :platforms="selectedPlatforms"
      :caption="state.caption"
      :link="state.link"
      :post-targets="selectedPostTargetLabels"
      :whatsapp-enabled="isWhatsAppSelected"
      :whatsapp-body="renderedTemplatePreview"
      :whatsapp-has-image-header="templateUsesImageHeader"
      :whatsapp-image-url="isReel ? '' : imagePreviewUrl"
    />

    <WhatsappTemplateDialog
      ref="templateDialogRef"
      :is-loading="isCreatingTemplate"
      @create="handleCreateTemplate"
    />
  </div>
</template>
